import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const {
    resetPassword
  } = useAuth();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    // Validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error: any) {
      console.error('Error sending reset email:', error);
      let errorMessage = 'Failed to send reset email. Please try again.';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-50">
      <div className="max-w-md w-full" data-id="element-51">
        <div className="text-center mb-10" data-id="element-52">
          <h1 className="text-4xl font-bold mb-2" data-id="element-53">Reset Password</h1>
          <p className="text-gray-600" data-id="element-54">
            Enter your email to reset your password
          </p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-55">
          {success ? <div className="text-center" data-id="element-56">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md" data-id="element-57">
                <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-58">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-59" />
                </svg>
                <h3 className="text-lg font-semibold text-green-900 mb-2" data-id="element-60">
                  Password reset successful!
                </h3>
                <p className="text-sm text-green-700" data-id="element-61">
                  In a real app, we would send a reset link to{' '}
                  <strong data-id="element-62">{email}</strong>
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-6" data-id="element-63">
                <strong data-id="element-64">Demo Mode:</strong> Since this is a demo, your password
                has been reset. You can now sign in with any password.
              </p>

              <button onClick={() => navigate('/signin')} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-65">
                Back to Sign In
              </button>
            </div> : <>
              <h2 className="text-2xl font-semibold mb-6" data-id="element-66">
                Forgot your password?
              </h2>

              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm" data-id="element-67">
                <strong data-id="element-68">Demo Mode:</strong> This is a mock authentication
                system. Password reset will simulate success.
              </div>

              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-id="element-69">
                  {error}
                </div>}

              <form onSubmit={handleSubmit} data-id="element-70">
                <div className="mb-6" data-id="element-71">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-72">
                    Email address
                  </label>
                  <input id="email" type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                if (error) setError('');
              }} placeholder="you@company.com" className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required disabled={isLoading} data-id="element-73" />
                </div>

                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out flex items-center justify-center mb-4" disabled={isLoading} data-id="element-74">
                  {isLoading ? <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-75">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-76"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-77"></path>
                      </svg>
                      Processing...
                    </> : 'Reset Password'}
                </button>

                <button type="button" onClick={() => navigate('/signin')} className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-78">
                  Back to Sign In
                </button>
              </form>
            </>}
        </div>

        <div className="text-center" data-id="element-79">
          <p className="text-gray-600" data-id="element-80">
            Don't have an account?{' '}
            <a href="/signup" className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-81">
              Sign up
            </a>
          </p>
        </div>
      </div>
    </div>;
}